import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import '../../../styles/admin/UsersPending.css';
import { Users } from '../../../models/User.tsx';
import { AnyBet, searchBetsUsers } from '../../../services/admin/BetsService.tsx';
import { FiAlertCircle } from "react-icons/fi";
import { RiFileSearchLine } from "react-icons/ri";
import { BetDetails } from '../../../models/MyBetModel.tsx';
import { formatNumbers, isFreePlayBet } from '../../../helpers/functions.tsx';
import { useUser } from '../../../context/UserContext.tsx';
import AgentContext from '../../../context/admin/Agent.tsx';
import { BetDetailsBox } from '../pending/BetDetailsBox.tsx';
import { FaSyncAlt } from 'react-icons/fa';
import { cachedFetch } from '../../../helpers/queryCache.tsx';
import { formatAdminFiguresDateTime } from '../../../helpers/adminFiguresTime.tsx';

const DAYS_MAPPING: Record<string, number> = {
    day7: 7,
    day30: 30,
    day60: 90,
};

const DELETED_WAGERS_CACHE_TTL_MS = 30_000;
const PLAYER_SUGGESTION_LIMIT = 10;

const isParlay = (bet: AnyBet): bet is BetDetails => 'selections' in bet;

const hasOdd = (bet: AnyBet): bet is BetDetails => 'odd' in bet;

const filterUsers = (users: Users[], query: string) => {
    const text = query.toLowerCase().trim();
    if (!text) return [];

    return users.filter(user => {
        const userName = (user.userName || "").toLowerCase();
        const name = (user.name || "").toLowerCase();
        const pin = String(user.pin || "").toLowerCase();

        return userName.includes(text) || name.includes(text) || pin.includes(text);
    });
};

type DeletedWagerRowProps = {
    bet: AnyBet;
    isExpanded: boolean;
    onToggle: (ticket: string) => void;
    currentUser: Users | null;
};

const DeletedWagerRow = React.memo(({ bet, isExpanded, onToggle, currentUser }: DeletedWagerRowProps) => {
    const ticket = bet.ticket || '';

    return (
        <>
            <tr onClick={() => onToggle(ticket)} className="cursor">
                <td>{bet.player?.userName}</td>
                <td>{bet.dateDelete ? formatAdminFiguresDateTime(bet.dateDelete.toDate()) : ''}</td>
                <td>
                    {hasOdd(bet) && bet.odd?.name}<br />
                    {bet.odd?.market}
                    {isParlay(bet) && bet.selections?.map((s, i) => (
                        <div key={i} className="parlay-line">
                            {s.odd?.name}
                            {s.odd?.market}
                        </div>
                    ))}
                    {isFreePlayBet(bet) && (
                        <span className="bet-free">{bet.freeBet || 'FP'}</span>
                    )}
                </td>
                <td>
                    {bet.descriptionDelete?.includes("after") && (<span className="text-danger ms-1">*</span>)}
                    {bet.descriptionDelete}
                </td>
                <td>{formatNumbers(bet.bet)}</td>
                <td>{formatNumbers(bet.reward)}</td>
                <td>{bet.deleteBy}</td>
            </tr>
            {isExpanded && (
                <BetDetailsBox bet={bet} currentUser={currentUser} />
            )}
        </>
    );
});

DeletedWagerRow.displayName = 'DeletedWagerRow';

const DeletedWagers = () => {
    const { user: currentUser } = useUser();
    const { allPlayers, allAgents } = useContext(AgentContext);
    const [playerName, setPlayerName] = useState('');
    const [bets, setBets] = useState<AnyBet[]>([]);
    const [activeInput, setActiveInput] = useState<'agent' | 'player' | null>(null);
    const [range, setRange] = useState('day7');
    const [isLoading, setIsLoading] = useState(false);
    const [noDataMessage, setNoDataMessage] = useState('');
    const [expandedBets, setExpandedBets] = useState<Set<string>>(() => new Set());

    const playerNameRef = useRef(playerName);
    playerNameRef.current = playerName;
    const noDataTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
    const fetchIdRef = useRef(0);

    const playerResults = useMemo(
        () => filterUsers(allPlayers, playerName).slice(0, PLAYER_SUGGESTION_LIMIT),
        [allPlayers, playerName]
    );

    const showNoDataMessage = useCallback(() => {
        setNoDataMessage('No deleted bets found.');
        clearTimeout(noDataTimeoutRef.current);
        noDataTimeoutRef.current = setTimeout(() => {
            setNoDataMessage('');
        }, 5000);
    }, []);

    const fetchBets = useCallback(async (player: string, force = false) => {
        if (!currentUser?.userType || !currentUser?.userName) {
            return;
        }

        const { userType, userName } = currentUser;
        const daysRange = DAYS_MAPPING[range];
        const cacheKey = `deletedWagers:${userName}:${userType}:${range}:${player}`;
        const requestId = ++fetchIdRef.current;

        setIsLoading(true);
        setNoDataMessage('');

        const searchOptions = userType === 'masterAgent'
            ? { agentScope: allAgents.map(agent => agent.userName) }
            : undefined;

        try {
            const results = await cachedFetch(
                cacheKey,
                () => searchBetsUsers(userName, daysRange, userType, player || undefined, searchOptions),
                { ttlMs: DELETED_WAGERS_CACHE_TTL_MS, force }
            );

            if (requestId !== fetchIdRef.current) return;

            if (results.length === 0) {
                setBets([]);
                showNoDataMessage();
            } else {
                setBets(results);
            }
        } catch (error) {
            if (requestId !== fetchIdRef.current) return;
            console.error(error);
        } finally {
            if (requestId === fetchIdRef.current) {
                setIsLoading(false);
            }
        }
    }, [currentUser, range, showNoDataMessage, allAgents]);

    useEffect(() => {
        fetchBets(playerNameRef.current);
    }, [currentUser?.userName, currentUser?.userType, range, fetchBets]);

    useEffect(() => {
        return () => clearTimeout(noDataTimeoutRef.current);
    }, []);

    const handleRangeChange = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
        setRange(event.target.value);
        setExpandedBets(new Set());
    }, []);

    const handleSearch = useCallback(() => {
        fetchBets(playerName, true);
    }, [fetchBets, playerName]);

    const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setPlayerName(e.target.value);
    }, []);

    const handleSelectUser = useCallback((user: Users) => {
        setPlayerName(user.userName);
        setActiveInput(null);
    }, []);

    const handleToggleBet = useCallback((ticket: string) => {
        if (!ticket) return;
        setExpandedBets(prev => {
            const next = new Set(prev);
            if (next.has(ticket)) {
                next.delete(ticket);
            } else {
                next.add(ticket);
            }
            return next;
        });
    }, []);

    return (
        <div className='container-fluid'>
            <div className='mt-2 container-body'>
                <div className="userPendig-container mt-1">
                    <div className="user-row">
                        <div className="input-group input-group-sm flex-nowrap">
                            <span className="input-group-text body-customer">Players</span>
                            <input
                                className="inputTransaction body-customer"
                                placeholder="Players"
                                name="player"
                                value={playerName}
                                onFocus={() => setActiveInput('player')}
                                onChange={handleInputChange}
                                autoComplete="off" />
                            {activeInput === 'player' && playerResults.length > 0 && (
                                <ul className="list-group">
                                    {playerResults.map((user) => (
                                        <li key={user.id} className="list-group-item" onClick={() => handleSelectUser(user)}>
                                            <div className='d-flex justify-content-between'>
                                                <div>
                                                    <span className='search-username-navbar'>{user.userName}</span>
                                                    <span className='search-pin-name-navbar'>({user.pin})</span>
                                                </div>
                                                <div>
                                                    <span className='search-pin-name-navbar'>{user.name}</span>
                                                </div>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                        <div className="colum-pending">
                            <select
                                id="transaction"
                                name="transaction"
                                className="selectTransaction body-customer"
                                value={range}
                                onChange={handleRangeChange}
                            >
                                <option value="day7">7 Days</option>
                                <option value="day30">30 Days</option>
                                <option value="day60">90 Days</option>
                            </select>
                        </div>
                        <div className="col-auto">
                            <button className="btn btn-outline-info btn-xl" onClick={handleSearch}>
                                <RiFileSearchLine />
                            </button>
                        </div>
                    </div>
                </div>

                <div className="table-responsive header-table-customer">
                    {isLoading ? (
                        <div className="loader-container">
                            <FaSyncAlt className="loader-icon-proyect" />
                        </div>
                    ) : (
                        <table className='mt-3 table'>
                            <thead className='header-customer'>
                                <tr>
                                    <th className=''>Customer</th>
                                    <th className=''>Deleted (PST)</th>
                                    <th className=''>Bet</th>
                                    <th className=''>Description</th>
                                    <th className=''>Risk</th>
                                    <th className=''>To Win</th>
                                    <th className=''>Deleted By</th>
                                </tr>
                            </thead>
                            <tbody className="body-customer">
                                {bets.map((bet) => (
                                    <DeletedWagerRow
                                        key={bet.ticket}
                                        bet={bet}
                                        isExpanded={expandedBets.has(bet.ticket || '')}
                                        onToggle={handleToggleBet}
                                        currentUser={currentUser}
                                    />
                                ))}
                            </tbody>
                        </table>
                    )}
                    {!isLoading && noDataMessage && (
                        <div className="no-data-message">
                            <div className="no-data-message-content">
                                <FiAlertCircle className="no-data-icon" />
                                <span>{noDataMessage}</span>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DeletedWagers;
